import cn from "@utils/cn";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import React, { FC, useMemo, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { Article, ImageElement } from "@/types";

import RichText from "../RichText/RichText";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

interface Props {
  article: Article;
  isFirstArticle: boolean;
  isOffer?: boolean;
  customClass?: string;
  fallbackImage?: ImageElement;
}

const ArticleCard: FC<Props> = ({ article, isFirstArticle, isOffer, customClass, fallbackImage }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const locale = useLocale();
  const t = useTranslations();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const togglePlay = () => {
    if (videoRef.current?.paused) {
      videoRef.current?.play();
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
    ctaClickTracking("See video", "See video", "Article Card");
  };

  const articleTags = useMemo(() => {
    return (
      article.tags &&
      article.tags?.length > 0 && (
        <div className={cn("text-s ", isOffer ? "absolute top-xs left-xs" : "mt-xl font-secondary-light flex gap-xxs")}>
          <span className={cn(isOffer && "w-fit box-material-secondary inline-block text-white uppercase p-[6px] rounded-md mr-xs")} key={`tag-0`}>
            {t("article.tag." + article.tags[0])}
          </span>
          {article.tags.length > 1 && !isOffer && (
            <>
              <span className="mx-xs">•</span>
              <span
                className={cn(isOffer && "w-fit box-material-secondary inline-block text-white uppercase p-[6px] rounded-md mr-xs")}
                key={`tag-1`}
              >
                {t("article.tag." + article.tags[1])}
              </span>
            </>
          )}
        </div>
      )
    );
  }, [article.tags, isOffer]);

  const articleLink = useMemo(() => {
    return `/${locale}/${["industry-news", "news", "press-releases"].includes(article.articleType) ? "/" : ""}${article.articleType}/${article.slug}`;
  }, [article.articleType, article.slug, locale]);

  return (
    <>
      {article?.localePublished && (
        <a
          href={articleLink}
          className={cn(
            "relative mb-l md:mb-0",
            (!isFirstArticle || isMobile) && "flex flex-col rounded-lg shadow-strong overflow-hidden",
            isFirstArticle && !isMobile && "md:col-span-4 ",
            !article.video && "cursor-pointer",
            customClass && customClass,
            isFirstArticle && isOffer
              ? "group block"
              : `${isFirstArticle && !isMobile ? "aspect-16-9" : "aspect-2-3"} rounded-lg shadow-strong bg-top bg-no-repeat flex items-end overflow-hidden`,
          )}
        >
          {!isOffer && (article.image || fallbackImage) && (
            <div
              className={`${isFirstArticle && !isMobile ? "absolute top-0 left-0 w-full h-full z-0 aspect-video" : `relative aspect-video ${isMobile && !isFirstArticle ? "h-[70%]" : "h-[50%]"} w-full`}`}
            >
              <NextImage
                src={article.image?.url || fallbackImage?.url || ""}
                alt={article.image?.title || fallbackImage?.title || ""}
                fill
                className={`${isFirstArticle ? "object-cover" : "object-cover"} aspect-video`}
                priority
              />
            </div>
          )}
          {article.video && <div className="absolute top-0 left-0 bottom-0 right-0"></div>}

          {isOffer && (article.image || fallbackImage) && (
            <div className="relative overflow-hidden aspect-4-3 rounded-lg">
              <Image
                alt={article.image?.title || fallbackImage?.title || ""}
                className="aspect-4-3 transition transform hover:duration-[6000ms] group-hover:scale-105 motion-reduce:transition-none motion-reduce:hover:transform-none"
                src={article.image?.url || fallbackImage?.url || ""}
                width={article.image?.width || fallbackImage?.width || 550}
                height={article.image?.height || fallbackImage?.height || 420}
              />
              <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-4"></div>
              {articleTags}
            </div>
          )}
          <div
            className={cn(
              `w-full  relative z-[2] pb-[25px] ${!isFirstArticle && "h-[35%] md:h-[45%]"}`,
              "bg-white",
              isFirstArticle && "md:w-[60%] md:m-m md:rounded-lg",
              isOffer ? "pt-m" : "p-s",
            )}
          >
            {isOffer && <div className="mb-s font-secondary-light text-s uppercase">Commercial Offer</div>}
            {!article.video && (article.postDate || article.articleType) && (
              <div className="mb-s font-secondary-light text-s">
                {article.postDate && (
                  <>
                    <span>
                      {new Date(article.postDate).toLocaleString("default", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </>
                )}
                {article.postDate && article.articleType && <span className="mx-xs">•</span>}
                <span className="capitalize">{article.articleType.replace("-", " ")}</span>
              </div>
            )}
            <h3 className="text-m font-medium">{article.internalName}</h3>
            {isOffer ? (
              <p className="mt-s font-secondary-light text-s truncate-3">
                <RichText content={{ html: article.summary }} />
              </p>
            ) : (
              !article.video && articleTags
            )}
            {article.video && (
              <>
                <Button
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.WHITE}
                  onClick={(e) => {
                    e.preventDefault();
                    setModalOpen(true);
                  }}
                  customClass="mt-m font-medium"
                >
                  <span className="flex items-center gap-xs ">
                    <Icon name="play" size="16" title="Play Video" />
                    See video
                  </span>
                </Button>
              </>
            )}
          </div>
        </a>
      )}
      {article.video && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <ModalContent className="w-full !rounded-none py-l">
            <div className="flex justify-between border-gray-10 border-b-[1px] pb-m mb-l">
              <p className="font-primary font-medium text-m uppercase">{article.internalName}</p>
              <Button
                className="modal__close"
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => setModalOpen(false)}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title="close" className="stroke-black" />
                  <span className="sr-only">{t("button.label.close")}</span>
                </span>
              </Button>
            </div>
            <div className={`w-full h-[calc(100dvh-128px)]  md:h-[calc(100dvh-114px)] relative`}>
              <video className="object-cover w-full h-full rounded-lg" ref={videoRef} poster={article.image?.url || fallbackImage?.url} muted loop>
                <source src={article.video.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <Button
                size={ButtonSize.SMALL}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                onClick={() => togglePlay()}
                customClass="flex items-center border-none  font-medium absolute bottom-l right-l rounded-full"
              >
                <Icon name={isPlaying ? "pause" : "play"} size="16" title="play" className="pointer-events-none" />
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ArticleCard;
